/**
 * Meeting-guest-link helpers (Product B standalone meetings).
 */

/**
 * Default lifetime for an owner-minted "copy guest link" — 24h. `maxJoins` is
 * intentionally omitted at the call site (unlimited-within-TTL), matching a
 * "share this meeting" link like a Zoom link. The backend independently caps
 * `ttlSeconds` at MEET_INVITE_MAX_TTL_SECONDS, so an over-long value here is
 * rejected server-side regardless.
 */
export const MEETING_GUEST_LINK_TTL_SECONDS = 24 * 60 * 60;

/**
 * Best-effort, COSMETIC read of the `context.user.moderator` claim from a
 * (base64url, UNVERIFIED) Jitsi conference JWT payload segment.
 *
 * This is NOT a security check — the token's signature is never verified
 * here, and nothing stops a client from decoding/re-encoding a fake
 * `moderator: true` claim locally. It only decides whether to RENDER the
 * "copy guest link" button on `MeetJoinPage`. The real owner gate is
 * server-side: `POST /umbra/meet/meetings/invites` re-derives the caller's
 * identity from their auth session and returns the same generic 404 to a
 * non-owner as to an unknown meeting (no existence leak) — so a spoofed
 * claim here can, at worst, show a button whose click then fails harmlessly.
 *
 * Fails CLOSED: any decode/parse failure (malformed JWT, missing payload
 * segment, non-base64url, non-JSON, unexpected shape) returns `false` — i.e.
 * hide the button rather than risk mis-showing it.
 */
export function readModeratorClaim(token: string): boolean {
  try {
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment) {
      return false;
    }
    const json = base64UrlDecode(payloadSegment);
    const payload = JSON.parse(json) as {
      context?: { user?: { moderator?: boolean } };
    };
    return payload?.context?.user?.moderator === true;
  } catch {
    return false;
  }
}

/** Browser-safe base64url decode: url-safe chars -> standard + padding, then atob. */
function base64UrlDecode(segment: string): string {
  const std = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = std + "=".repeat((4 - (std.length % 4)) % 4);
  return atob(padded);
}
